import { useState } from "react";
import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/feature/PageHeader";
import useSeo from "@/hooks/useSeo";

const values = [
  { icon: "ri-compass-3-line", title: "网站定位", text: "漫番志定位为一站式卡通动漫综合门户，以清晰分类与实时排行帮助追番用户快速发现好作品。" },
  { icon: "ri-heart-line", title: "发展理念", text: "我们相信整理好作品本身就是一种热爱。用心选片、用心分类，让每一次追番都更省心、更有温度。" },
  { icon: "ri-scales-3-line", title: "内容原则", text: "坚持客观整理作品资料与口碑热度，不堆砌关键词，不做标题党，只呈现真正值得被看见的作品。" },
  { icon: "ri-shield-check-line", title: "版权尊重", text: "漫番志主要提供分类导航与资料整理，鼓励大家在正版平台观看，共同支持创作者的用心之作。" },
];

const principles = [
  "分类清晰，新番、剧场版、OVA、经典按类整理，入口直达",
  "排行实时更新，口碑热度一目了然",
  "作品资料详尽，剧情、主创、标签一应俱全",
  "内容客观真实，拒绝标题党与无意义堆砌",
  "响应式体验，电脑、平板、手机都能流畅浏览",
];

const testimonials = [
  { name: "柚子", role: "漫龄八年", text: "分类做得细，找剧场版和OVA特别方便，是我常用的追番入口。" },
  { name: "老白", role: "补番党", text: "经典榜很靠谱，按评分补番基本不踩雷，已经看完好几部神作。" },
  { name: "Mika", role: "新番追更", text: "每季新番都能快速看到，连载状态也标得清楚，体验很棒。" },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get("phone_alt") as string | null)?.trim() || "";
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    setStatus("loading");
    setFormError("");
    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "phone_alt") return;
      payload.append(key, value as string);
    });
    try {
      const res = await fetch("https://readdy.ai/api/form/da28tcjk8r5te5mqsjq0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试");
    }
  };

  useSeo({
    title: "关于我们 - 漫番志品牌介绍与联系方式",
    description:
      "了解漫番志的品牌介绍、网站定位、发展理念与内容原则，查看用户口碑，并获取我们的联系方式。漫番志是卡通动漫综合分类门户。",
    keywords: "漫番志,关于我们,动漫门户,追番平台,动漫分类,联系我们",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://freshanime.github.io/" },
          { "@type": "ListItem", position: 2, name: "关于我们" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于漫番志",
        description: "漫番志品牌介绍、定位与发展理念。",
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系我们",
        description: "漫番志联系方式与咨询表单。",
      },
    ],
  });

  return (
    <Layout>
      <PageHeader
        title="关于漫番志"
        subtitle="我们是热爱动漫的一群人，致力于把追番这件小事做得更省心、更有温度。"
        eyebrow="About Us"
        crumbs={[{ label: "关于我们" }]}
      />

      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <section className="grid items-center gap-8 lg:grid-cols-2">
          <div>
            <h2 className="font-heading text-2xl text-foreground-950">品牌介绍</h2>
            <p className="mt-4 leading-loose text-foreground-700">
              漫番志成立于追番文化日益繁荣的时代，我们深知每一位动漫爱好者面对海量作品时的迷茫——新番更新频繁、剧场版与
              OVA 分散各处、热门作品层出不穷，如何快速找到真正值得看的那一部？
            </p>
            <p className="mt-4 leading-loose text-foreground-700">
              于是我们打造了这座一站式动漫综合分类门户：新番速递、剧场版、OVA、经典典藏按类整理，热门排行实时更新，
              每一部作品都附有详尽的资料与口碑评分。从这里开始，追番不再无从下手。
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl ring-1 ring-background-300/60">
            <img
              src="https://readdy.ai/api/search-image?query=Cozy%20anime%20collection%20corner%20with%20posters%20and%20shelves%20of%20figurines%20under%20warm%20amber%20and%20teal%20lighting%2C%20nostalgic%20otaku%20room%2C%20vibrant%20yet%20warm%20atmosphere%2C%20cinematic%2C%20high%20detail&width=1200&height=800&seq=about-brand-2026&orientation=landscape"
              alt="漫番志品牌理念场景图"
              title="漫番志 - 动漫爱好者的追番天地"
              className="aspect-[3/2] w-full object-cover object-center"
            />
          </div>
        </section>

        <section className="mt-16">
          <h2 className="mb-8 font-heading text-2xl text-foreground-950">定位 · 理念 · 原则</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {values.map((v) => (
              <div
                key={v.title}
                className="rounded-2xl bg-background-100 p-6 ring-1 ring-background-300/60 transition-all hover:-translate-y-1 hover:ring-primary-500/50"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 text-background-50">
                  <i className={`${v.icon} text-xl`} />
                </span>
                <h3 className="mt-4 font-heading text-lg text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-2xl bg-background-100 p-6 ring-1 ring-background-300/60 md:p-8">
            <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
              <i className="ri-list-check-3 text-accent-400" /> 我们的内容原则
            </h3>
            <ul className="mt-4 space-y-3">
              {principles.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-foreground-700">
                  <i className="ri-checkbox-circle-fill mt-0.5 text-accent-400" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-16">
          <h2 className="mb-8 font-heading text-2xl text-foreground-950">用户口碑</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-2xl bg-background-100 p-6 ring-1 ring-background-300/60">
                <i className="ri-double-quotes-l text-2xl text-secondary-400" />
                <blockquote className="mt-2 text-sm leading-relaxed text-foreground-700">{t.text}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-sm font-bold text-background-50">
                    {t.name.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="mt-16 grid gap-6 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              无论是合作建议、内容反馈还是追番心得，都欢迎随时与我们联系，我们会认真阅读每一封来信。
            </p>
            <ul className="mt-6 space-y-4 text-sm">
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-background-100 text-primary-400 ring-1 ring-background-300/60">
                  <i className="ri-mail-line" />
                </span>
                hello@manfanzhi.cn
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-background-100 text-accent-400 ring-1 ring-background-300/60">
                  <i className="ri-chat-3-line" />
                </span>
                官方微博：@漫番志
              </li>
              <li className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-background-100 text-secondary-400 ring-1 ring-background-300/60">
                  <i className="ri-global-line" />
                </span>
                更新中，敬请关注
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <form
              data-readdy-form="联系我们咨询表单"
              onSubmit={handleSubmit}
              className="space-y-4 rounded-2xl bg-background-100 p-6 ring-1 ring-background-300/60 md:p-8"
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-bold text-foreground-950">你的称呼</label>
                  <input
                    id="name"
                    name="name"
                    required
                    placeholder="怎么称呼你"
                    className="w-full rounded-lg bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none ring-1 ring-background-300/60 placeholder:text-foreground-500 focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-bold text-foreground-950">邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于回复你"
                    className="w-full rounded-lg bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none ring-1 ring-background-300/60 placeholder:text-foreground-500 focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="mb-1.5 block text-sm font-bold text-foreground-950">主题</label>
                <select
                  id="subject"
                  name="subject"
                  className="w-full rounded-lg bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none ring-1 ring-background-300/60 focus:ring-2 focus:ring-primary-500"
                >
                  <option>合作建议</option>
                  <option>内容反馈</option>
                  <option>作品推荐</option>
                  <option>其他咨询</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="mb-1.5 block text-sm font-bold text-foreground-950">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请在这里写下你的想法…"
                  className="w-full resize-none rounded-lg bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none ring-1 ring-background-300/60 placeholder:text-foreground-500 focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
              </div>
              <input
                type="text"
                name="phone_alt"
                className="trap-guard-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="whitespace-nowrap rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-2.5 text-sm font-bold text-background-50 transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                {status === "loading" ? "提交中…" : "发送留言"}
              </button>
              {status === "success" && (
                <p className="flex items-center gap-1.5 text-sm text-accent-400">
                  <i className="ri-checkbox-circle-fill" /> 留言已收到，感谢你的来信！
                </p>
              )}
              {status === "error" && (
                <p className="flex items-center gap-1.5 text-sm text-primary-400">
                  <i className="ri-error-warning-fill" /> {formError}
                </p>
              )}
            </form>
          </div>
        </section>
      </div>
    </Layout>
  );
}